import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * PdfModalLink
 * - If you pass `pdf` => shows iframe
 * - If you pass `content` => renders React node (best for JSX CV)
 * - If you pass `html` => renders raw HTML string (be careful, sanitize if untrusted)
 */
export default function PdfModalLink({
  pdf,              // string URL
  content,          // React node
  html,             // string HTML
  children,
  title = "Preview",
  width = 820,
  height = 600,
  maxWidth = "94vw",
  maxHeight = "88vh",
}) {
  const [open, setOpen] = useState(false);

  const mode = useMemo(() => {
    if (content) return "content";
    if (html) return "html";
    if (pdf) return "pdf";
    return "empty";
  }, [content, html, pdf]);

  useEffect(() => {
    if (!open) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <a
        href={pdf || "#"}
        onClick={(e) => {
          e.preventDefault();
          setOpen(true);
        }}
        style={{ cursor: "pointer", color:'white', padding:'1rem 0' }}
      >
        {children}
      </a>

      <AnimatePresence>
        {open && (
          <motion.div
            style={styles.backdrop}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
            initial="hidden"
            animate="show"
            exit="exit"
            variants={backdropVariants}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={title}
              style={{
                ...styles.modal,
                width,
                height,
                maxWidth,
                maxHeight,
              }}
              onMouseDown={(e) => e.stopPropagation()}
              initial="hidden"
              animate="show"
              exit="exit"
              variants={modalVariants}
            >
              <div style={styles.header}>
                <div style={styles.headerLeft}>
                  <div style={styles.title}>{title}</div>
                  <div style={styles.subtitle}>
                    {mode === "pdf" ? "PDF preview" : "Preview"}
                  </div>
                </div>

                <div style={styles.headerRight}>
                  {mode === "pdf" && (
                    <a
                      href={pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.actionLink}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      Open in new tab
                    </a>
                  )}

                  <button
                    style={styles.closeBtn}
                    onClick={() => setOpen(false)}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div style={styles.body}>
                {mode === "pdf" ? (
                  <div style={styles.iframeShell}>
                    <iframe src={pdf} title={title} style={styles.iframe} />
                  </div>
                ) : mode === "content" ? (
                  <div style={styles.contentShell}>{content}</div>
                ) : mode === "html" ? (
                  <div
                    style={styles.contentShell}
                    // ⚠️ only do this with trusted HTML or sanitize first
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                ) : (
                  <div style={styles.empty}>
                    Nothing to show (pass `pdf`, `content`, or `html`)
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const backdropVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.18, ease: "easeOut" } },
  exit: { opacity: 0, transition: { duration: 0.14, ease: "easeIn" } },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.96, y: 18 },
  show: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", stiffness: 420, damping: 34, mass: 0.9 },
  },
  exit: { opacity: 0, scale: 0.98, y: 10, transition: { duration: 0.12, ease: "easeIn" } },
};

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
  },
  modal: {
    borderRadius: 18,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(255,255,255,0.35)",
    boxShadow: "0 24px 70px rgba(0,0,0,0.35), 0 2px 0 rgba(255,255,255,0.45) inset",
  },
  header: {
    padding: "14px 14px 12px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    background: "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
  },
  headerLeft: { display: "flex", flexDirection: "column", gap: 2 },
  title: { fontSize: 14, fontWeight: 800, letterSpacing: 0.2 },
  subtitle: { fontSize: 12, opacity: 0.65 },
  headerRight: { display: "flex", alignItems: "center", gap: 10 },
  actionLink: {
    fontSize: 12,
    textDecoration: "none",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.7)",
  },
  closeBtn: {
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.7)",
    borderRadius: 999,
    width: 34,
    height: 34,
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    fontSize: 16,
    lineHeight: 1,
  },
  body: { flex: 1, minHeight: 0 },
  iframeShell: { padding: 10, background: "rgba(0,0,0,0.04)", height: "100%" },
  iframe: {
    width: "100%",
    height: "100%",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 12,
    background: "#fff",
  },
  contentShell: {
    height: "100%",
    overflow: "auto",
    padding: 16,
    background: "rgba(0,0,0,0.03)",
  },
  empty: { padding: 16, opacity: 0.75 },
};